import { useTranslation } from "react-i18next";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import { apiClient, revalidateApiQuery } from "../../../../services/api";
import { saveAvatarPick } from "../../../../domains/user/saveAvatarPick";
import { defaultAvatarImages } from "../../../../domains/user/utils/pickUserImage";
import {
  useAvatarPicker,
  setPickedAvatar,
  resetAvatarPicker,
} from "../../../../domains/user/stores/avatarPickerStore";
import { displayToast } from "../../../app/AppToast";
import { Button } from "../../../ui/Button";
import Loader from "../../../Loader";
import CustomAvatarUploader from "../../../profile/CustomAvatarUploader";
import { popSettingsView } from "../../../../domains/app/stores/settingsSheetStore";
import { SettingsSubHeader } from "../SettingsSubHeader";

export function CertificationView(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const pick = useAvatarPicker();

  if (currentUser === null) {
    return (
      <div className="flex h-full flex-col">
        <SettingsSubHeader title={t("Lemon certified request")} />
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      </div>
    );
  }

  // Certification requires a *custom* uploaded photo — a default avatar doesn't
  // count (mirrors the legacy RequestCertificationPage requireUpload logic).
  const pic = currentUser.profilePicture;
  const customUrl =
    pic !== null && pic.image !== null && pic.image.url !== ""
      ? pic.image.url
      : "";
  const defaultUrl =
    pic !== null && pic.name !== null
      ? defaultAvatarImages[pic.name].default
      : "";
  // This view never offers removal, so a `removed` pick can only be leftover
  // state from the avatar view — it means "no custom photo", same as none.
  const pickedDataUrl = pick.status === "picked" ? pick.dataUrl : null;
  const displayedUrl =
    pickedDataUrl !== null
      ? pickedDataUrl
      : customUrl !== ""
        ? customUrl
        : defaultUrl;
  const isNewUpload = pickedDataUrl !== null;
  const canSubmit = customUrl !== "" || isNewUpload;

  async function submit(): Promise<void> {
    if (pickedDataUrl !== null) {
      // Only the `picked` branch: this view never offers removal, so a leftover
      // `removed` pick from the avatar view must not delete the photo here.
      const uploaded = await saveAvatarPick({
        status: "picked",
        dataUrl: pickedDataUrl,
      });
      if (uploaded === "failed") {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      await reloadCurrentUser();
    }
    try {
      await apiClient.post("/webapp/private/lemon-certified/request");
      displayToast(t("Certification request sent"), "success");
      resetAvatarPicker();
      void revalidateApiQuery("/webapp/private/lemon-certified/request", {
        durable: true,
      });
      popSettingsView();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={t("Lemon certified request")} />
      <div className="flex-1 overflow-y-auto scroll-fog-y px-5 pt-4 pb-12">
        <CustomAvatarUploader
          avatarUrl={displayedUrl}
          fullname={currentUser.fullname}
          buttonText={
            displayedUrl !== ""
              ? t("Choose another picture")
              : t("Upload profile picture")
          }
          onChange={(url) => {
            setPickedAvatar(url);
          }}
        />
        <Button
          variant="primary"
          size="lg"
          className="mt-4 w-full"
          disabled={!canSubmit}
          onClick={() => {
            void submit();
          }}
        >
          {t("Request Certification")}
        </Button>
      </div>
    </div>
  );
}
